import { useSubscribeLink } from "@helper/checkMobileAppLink";
import {
  ENABLE_ESHOP_SUBSCRIBE,
  ESHOP_SUBSCRIBE_URL,
} from "@helper/getEnvVariables";
import {
  CD_LANGUAGE,
  useBHCustomEventAnalyticsCall,
} from "@hooks/useAnalyticsCall/useBHCustomEventAnalyticsCall";
import { useCallback, useEffect, useState } from "react";

import { isClientSide } from "./utils";

/**
 * EMTM-1621: subscribe-CTA behaviour for the eShop–eSaaS touchpoint.
 *
 * When the `ENABLE_ESHOP_SUBSCRIBE` flag is on, a subscribe CTA should:
 *  - link to the on-domain subscribe page with `?eshop_ref=<clickReference>`
 *    appended (the eShop funnel reads this reference), and
 *  - push a GA `click_reference` attribute to the website datalayer on click.
 *
 * `clickReference` identifies the placement: `subscribe-header`, `subscribe-footer`,
 * or `subscribe-usermenu`. The GA event fires with category `"subscribe"`,
 * action `"click"`, and language `CD_LANGUAGE.MALAY` — values agreed in EMTM-1621.
 *
 * When the flag is off this is a no-op: `href` is the existing subscribe link
 * and no datalayer event is pushed, preserving current behaviour.
 */

/**
 * Append eShop tracking params without a URL parser (works for relative +
 * absolute URLs).
 */
function withEshopParams(
  url: string,
  clickReference: string,
  articleId?: string,
): string {
  const hashIndex = url.indexOf("#");
  const urlWithoutHash = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const hash = hashIndex === -1 ? "" : url.slice(hashIndex);
  const querySeparator = urlWithoutHash.includes("?")
    ? urlWithoutHash.endsWith("?") || urlWithoutHash.endsWith("&")
      ? ""
      : "&"
    : "?";

  let trackingParams = `eshop_ref=${encodeURIComponent(clickReference)}`;
  if (articleId) {
    trackingParams += `&article_id=${encodeURIComponent(articleId)}`;
  }

  return `${urlWithoutHash}${querySeparator}${trackingParams}${hash}`;
}

export type EshopSubscribeCta = {
  /** href for the CTA anchor (with eshop_ref when the flag is on). */
  href: string;
  /** click handler that pushes click_reference to the datalayer (undefined when the flag is off). */
  onClick?: () => void;
  /** whether the subscribe CTA should render at all (false in the mobile webview). */
  isSubscribeAvailable: boolean;
};

export function useEshopSubscribeCta(
  clickReference: string,
): EshopSubscribeCta {
  const subscribeLink = useSubscribeLink();
  const bhCustomEvent = useBHCustomEventAnalyticsCall();
  const [articleId, setArticleId] = useState<string | undefined>(undefined);
  const enabled =
    ENABLE_ESHOP_SUBSCRIBE && !!ESHOP_SUBSCRIBE_URL && subscribeLink !== "";

  const syncArticleId = useCallback(() => {
    if (typeof window === "undefined") {
      setArticleId(undefined);
      return;
    }

    const data = (window as { _data?: { articleid?: string } })._data;
    setArticleId(data?.articleid || undefined);
  }, []);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;

    syncArticleId();
    window.addEventListener("gaDataUpdated", syncArticleId);

    return () => {
      window.removeEventListener("gaDataUpdated", syncArticleId);
    };
  }, [enabled, syncArticleId]);

  const onClick = useCallback(() => {
    if (!isClientSide) return;
    bhCustomEvent(
      "subscribe",
      "click",
      window.location.href,
      undefined,
      "",
      CD_LANGUAGE.MALAY,
      undefined,
      undefined,
      clickReference,
    );
  }, [bhCustomEvent, clickReference]);

  return {
    href: enabled
      ? withEshopParams(subscribeLink, clickReference, articleId)
      : subscribeLink,
    onClick: enabled ? onClick : undefined,
    isSubscribeAvailable: subscribeLink !== "",
  };
}
